import { memo, useCallback, useEffect, useState } from 'react'
import { Loader2, ExternalLink, X } from 'lucide-react'
import { toast } from 'sonner'
import { useSubscription } from '@/hooks/useSubscription'
import { useRenewSubscription } from '@/hooks/useRenewSubscription'
import { useUsdcBalance } from '@/hooks/useUsdcBalance'
import { useRedeemablePositions } from '@/hooks/useRedeemablePositions'
import { usePositions } from '@/hooks/usePositions'
import { useOpenOrders } from '@/hooks/useOpenOrders'
import { usePmWalletStore } from '@/store/pmWalletStore'
import { Button } from '@/components/ui/button'

const POLYMARKET_URL = 'https://polymarket.com'
const SUBSCRIPTION_USDC = 8.5

interface Props {
  /** Force-open (e.g. из PlanDialog). Если не передано — модалка сама
   * открывается при истечении подписки + PIN-unlock. */
  forceOpen?: boolean
  onClose?: () => void
}

/**
 * Модалка продления подписки. Авто-открывается, когда:
 *   1) PM-кошелёк разблокирован (pmProxyAddress != null)
 *   2) Сервер вернул exists:true (trial уже активирован)
 *   3) isActive === false (paid_until и trial_ends_at истекли)
 *
 * Если юзер закрыл — `dismissed` фиксируется на сессию, модалка не спамит.
 * Explicit блокеры (тап по ячейке в expired) поднимают модалку снова через
 * forceOpen.
 */
export const RenewalModal = memo(function RenewalModal({ forceOpen, onClose }: Props) {
  const { exists, isActive, address } = useSubscription()
  const pmProxyAddress = usePmWalletStore((s) => s.pmProxyAddress)
  const { balance } = useUsdcBalance()
  const { positions: redeemablePositions } = useRedeemablePositions(pmProxyAddress ?? undefined)
  const positionsMap = usePositions(pmProxyAddress ?? undefined)
  const openOrdersMap = useOpenOrders()
  const renew = useRenewSubscription()
  const [dismissed, setDismissed] = useState(false)

  const autoOpen = !!address && exists && !isActive && !dismissed
  const open = forceOpen || autoOpen

  useEffect(() => {
    // Сброс dismissed, когда юзер разлочил новый адрес / сменил PM-ключ
    setDismissed(false)
  }, [address])

  const hasRedeemables = redeemablePositions.length > 0
  const hasOpenPositions = Array.from(positionsMap.values()).some((p) => p.size > 0)
  const openOrdersCount = Array.from(openOrdersMap.values()).filter(
    (o) => (o.sizeTotal ?? 0) - (o.sizeMatched ?? 0) > 0
  ).length
  const hasOpenOrders = openOrdersCount > 0
  const showPolymarketLink = hasRedeemables || hasOpenPositions || hasOpenOrders

  const insufficientBalance = balance != null && balance < SUBSCRIPTION_USDC

  const handleRenew = useCallback(async () => {
    try {
      await renew.mutateAsync()
      toast.success('Подписка активирована')
      onClose?.()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Ошибка продления'
      toast.error(msg)
    }
  }, [renew, onClose])

  const handleClose = useCallback(() => {
    setDismissed(true)
    onClose?.()
  }, [onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="renew-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
        aria-label="Закрыть"
      />
      <div className="relative z-10 w-full max-w-sm rounded-xl border border-[#374151] bg-[#111827] p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 id="renew-title" className="text-lg font-semibold text-white">
            Подписка истекла
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="rounded p-1 text-[#94a3b8] hover:bg-white/10 hover:text-white"
            aria-label="Закрыть"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mb-4 rounded-lg border border-[#374151] bg-[#1f2937] px-4 py-3">
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-semibold text-[#22c55e]">PolyStrike Visual</span>
            <span className="text-sm text-white tabular-nums">{SUBSCRIPTION_USDC} USDC</span>
          </div>
          <div className="mt-1 text-[11px] text-[#94a3b8]">
            30 дней. Ключ остаётся у вас, списание подписывается в этой сессии.
          </div>
          {balance != null && (
            <div className="mt-2 text-[11px]">
              <span className="text-[#94a3b8]">Баланс USDC: </span>
              <span className={insufficientBalance ? 'text-[#ef4444]' : 'text-white'}>
                {balance.toFixed(2)}
              </span>
              {insufficientBalance && (
                <span className="text-[#ef4444]"> · недостаточно для продления</span>
              )}
            </div>
          )}
        </div>

        <div className="mb-3 space-y-2">
          <Button
            onClick={() => void handleRenew()}
            disabled={renew.isPending || insufficientBalance}
            className="w-full bg-[#22c55e] text-black hover:bg-[#16a34a]"
          >
            {renew.isPending ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Продление…</>
            ) : (
              `Продлить за ${SUBSCRIPTION_USDC} USDC`
            )}
          </Button>
          <button
            type="button"
            onClick={handleClose}
            className="w-full rounded-lg border border-[#374151] px-3 py-2 text-xs text-[#94a3b8] hover:bg-white/5 hover:text-white"
          >
            Продолжить без подписки
          </button>
        </div>

        {showPolymarketLink && (
          <div className="rounded-lg border border-[#f59e0b]/30 bg-[#f59e0b]/5 px-3 py-2.5 text-[11px] text-[#f59e0b]">
            <div className="mb-1.5 font-medium">
              {[
                hasOpenOrders && `${openOrdersCount} открытых ордер(ов)`,
                hasOpenPositions && 'активные позиции',
                hasRedeemables && 'выигрыши к клейму',
              ].filter(Boolean).join(' · ')}
            </div>
            <div className="mb-2 text-[#e2e8f0]/80">
              Управлять ими можно на polymarket.com — логин через тот же PM-кошелёк.
            </div>
            <a
              href={POLYMARKET_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[#f59e0b] hover:text-[#fbbf24]"
            >
              Открыть Polymarket
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}
      </div>
    </div>
  )
})
